import type { Theme } from "@mui/material/styles";

import { ACTION_TYPES } from "./ActionEditor";

/**
 * The TYPE and CODE chip treatments for the Actions catalogue, per the hand-off.
 *
 * WHY THIS EXISTS. The list rendered `color={type === "MENU_OPTION" ? "primary" : "default"}`
 * — two treatments. The design gives the type chip FOUR distinct fill/ink pairs plus a fifth
 * for a value it does not recognise (`Actions Pattern v2.dc.html:1021` and `:1075`):
 *
 *   MENU_OPTION   --surface-brand-light  + --brand-primary-dark
 *   ALERTS        --warning-light        + --warning-ink
 *   KPI           --surface-2            + --text-secondary
 *   REPORT        --success-light        + --success-ink
 *   unrecognised  --error-light          + --error-dark
 *
 * So ALERTS, KPI and REPORT all rendered as the same grey chip, and an unrecognised type
 * rendered as that grey too. The type column is the main thing an administrator scans this
 * table by, and it carried almost no signal.
 *
 * TOKENS, NOT LITERALS — the hand-off's rule ("bind to the token, never the literal"), so
 * each pair below names a palette role. The SIMS values it resolves to are
 * #E9D0F4/#52197B, #FFE7C2/#7A3200, #F0EEF1/#575C5C, #AEDD49/#2C5A02 and #FCD6D0/#AB0707;
 * all five clear WCAG AA 4.5:1, which `__tests__/pages/Actions/typeChipStyles.test.ts` pins.
 *
 * COLOUR IS NEVER THE ONLY SIGNAL (NFR-5). Every chip still shows the type as text, and an
 * unrecognised type keeps the "Unrecognised type" line and warning icon that SIMS-8710
 * added — the red fill is an addition to that wording, never a replacement for it.
 *
 * KNOWN GAP, raised with the design owner rather than papered over: the app recognises
 * EIGHT types (`ACTION_TYPES`) and the design styles FOUR. Its `KNOWN_TYPES` array is those
 * same four, so the hand-off simply never covered OPTION, EVENTS, SPECIAL_FEATURE or
 * System — which `source.md` already flags under OQ-3, the fixture set being narrower than
 * production. Those four take the neutral pair rather than a colour invented here. The
 * consequence is deliberate and worth stating: neutral is also KPI's designed pair, so KPI
 * is not distinguishable from them until the design names treatments for all eight.
 */

/** A chip's fill and text colour, both resolved from the theme. */
export interface ChipPair {
	bg: string;
	ink: string;
}

/**
 * The neutral pair — the design's KPI treatment, and the fallback for a recognised type the
 * hand-off never styled. Kept as one named value so it is visibly the *same* decision in
 * both places instead of two look-alike literals.
 */
const neutralPair = (theme: Theme): ChipPair => ({
	bg: theme.palette.surfaceTwo.main,
	ink: theme.palette.text.secondary,
});

/** The four pairs the hand-off specifies, keyed by the type they belong to. */
const designedPairs: Record<string, (theme: Theme) => ChipPair> = {
	MENU_OPTION: (theme) => ({
		bg: theme.palette.surfaceTint.main,
		ink: theme.palette.primary.dark,
	}),
	ALERTS: (theme) => ({
		bg: theme.palette.warning.light,
		ink: theme.palette.warningInk.main,
	}),
	KPI: neutralPair,
	REPORT: (theme) => ({
		bg: theme.palette.success.light,
		ink: theme.palette.successInk.main,
	}),
};

/** The fill/ink the design gives a value that is not one of the app's known types. */
const unrecognisedPair = (theme: Theme): ChipPair => ({
	bg: theme.palette.error.light,
	ink: theme.palette.error.dark,
});

/** Whether `type` is one of the app's recognised action types. */
export const isRecognisedActionType = (type: string): boolean =>
	ACTION_TYPES.includes(type);

/**
 * The fill and ink for one action type.
 *
 * Recognition is checked FIRST, against the app's eight types rather than the design's four:
 * keying straight off `designedPairs` would paint OPTION, EVENTS, SPECIAL_FEATURE and System
 * in the error colour, i.e. report four perfectly valid production types as faults.
 */
export const actionTypeChipPair = (theme: Theme, type: string): ChipPair => {
	if (!isRecognisedActionType(type)) {
		return unrecognisedPair(theme);
	}
	return (designedPairs[type] ?? neutralPair)(theme);
};

/**
 * `sx` for the TYPE chip, as a theme callback so the roles resolve at render time.
 *
 * `bgcolor`/`color` are set directly rather than through MUI's `color` prop because that
 * prop only offers the palette's named intents (primary/success/warning/…) with their
 * `main` fill and `contrastText` label — which is how the screen ended up with a dark
 * #408103 fill and white text where the design asks for a #AEDD49 fill with #2C5A02 ink.
 */
export const typeChipSx = (type: string) => (theme: Theme) => {
	const { bg, ink } = actionTypeChipPair(theme, type);
	return {
		bgcolor: bg,
		color: ink,
		// The chip's own metrics, from the design: 11px semibold with a touch of tracking in
		// a 22px pill (`Actions Pattern v2.dc.html:275`). It was set a size larger with no
		// tracking, which made the TYPE column heavier than the name beside it.
		fontSize: "0.6875rem",
		fontWeight: 600,
		letterSpacing: "0.04em",
		height: "1.375rem",
		"& .MuiChip-label": { px: 1.25 },
	};
};

/**
 * `sx` for the CODE cell.
 *
 * The design sets an action code in monospace in all nine places it appears
 * (`Actions Pattern v2.dc.html:271` for this cell: 11px mono, `--surface-2` fill, pill
 * radius, `--text-secondary`). The list rendered a default MUI chip in the UI face, which
 * is the one place the difference actually costs something — codes like WEB_ACTION_ADD and
 * WEB_ACTION_REGROUP are compared character by character, and a proportional face is
 * measurably worse at that than a monospaced one.
 */
export const codeChipSx = (theme: Theme) => ({
	bgcolor: theme.palette.surfaceTwo.main,
	color: theme.palette.text.secondary,
	fontFamily: "'SF Mono', Menlo, Consolas, monospace",
	fontSize: "0.6875rem",
	height: "1.5rem",
	borderRadius: "999px",
	"& .MuiChip-label": { px: 1.25 },
});

/**
 * `sx` for a member code on a GROUP's card — the strip of action codes along its foot.
 *
 * Deliberately not `codeChipSx`, though the two are cousins. The design sets a code in
 * MONOSPACE everywhere it appears, and that part is shared, but this strip is a smaller
 * instance of it: 10px in a pill padded 3px 8px (`Actions Pattern v2.dc.html:571`) against
 * the 11px, 24px-tall cell chip at `:271`. Up to four of these sit on one card beside a
 * "+N more", so they are sized to fit a row rather than to head a column.
 *
 * Folding them into one helper would mean a size argument on a style whose two callers want
 * two different sizes for two different reasons — the fill and the face are what they
 * actually share, and those are the theme roles below.
 */
export const memberCodeChipSx = (theme: Theme) => ({
	bgcolor: theme.palette.surfaceTwo.main,
	color: theme.palette.text.secondary,
	fontFamily: "'SF Mono', Menlo, Consolas, monospace",
	fontSize: "0.625rem", // 10px
	height: "1.25rem", // 10px type in a 3px-padded pill
	borderRadius: "999px",
	"& .MuiChip-label": { px: 1 }, // 8px
});

/**
 * `sx` for the group STATUS pill.
 *
 * ACTIVE takes the success fill/ink pair; anything else takes neutral. The status word is
 * always rendered as text, so this is colour *in addition to* the label (NFR-5).
 */
export const statusPillSx = (status: string) => (theme: Theme) => {
	const active = status === "ACTIVE";
	return {
		bgcolor: active
			? theme.palette.success.light
			: theme.palette.surfaceTwo.main,
		color: active
			? theme.palette.successInk.main
			: theme.palette.text.secondary,
		fontSize: "0.75rem",
		fontWeight: 500,
		height: "1.5rem",
		"& .MuiChip-label": { px: 1.25 },
	};
};

/**
 * `sx` for the count badge on each tab.
 *
 * The design puts a tab's count in a pill BESIDE the label rather than in parentheses
 * inside it (`Actions Pattern v2.dc.html:122`), and the fill follows selection (`:1233`):
 * the brand wash behind brand-dark ink on the selected tab, the neutral pair on the other.
 * The selected pair is the same one MENU_OPTION takes, so it reads from the same two roles
 * rather than being re-specified as a look-alike.
 *
 * HEIGHT. 16px, which is what SIMS-9141 asks for. MUI's Chip is 32px and `size="small"` is
 * 24px, so neither variant gives the design's height and it is set here. At 16px the
 * label's own padding is all that keeps the digits off the edge, and `px: 1` is the
 * design's 8px.
 */
export const tabCountChipSx = (selected: boolean) => (theme: Theme) => {
	const { bg, ink } = selected
		? { bg: theme.palette.surfaceTint.main, ink: theme.palette.primary.dark }
		: neutralPair(theme);
	return {
		bgcolor: bg,
		color: ink,
		height: "1rem", // 16px
		fontSize: "0.75rem", // 12px
		fontWeight: 500,
		borderRadius: "999px",
		"& .MuiChip-label": { px: 1 }, // 8px
	};
};

/**
 * `sx` for the warning-ink badges — "Moved — not saved" and "Unsaved changes".
 *
 * The design draws both as `--warning-light` behind `--warning-ink` at a pill radius
 * (`:435`, `:485`, `:600`, `:760`). MUI's `color="warning"` instead fills with
 * `warning.main` (#F8955B) — a saturated orange that reads as an error rather than as the
 * pending change it marks.
 *
 * To be accurate about what this is and is not: the MUI-filled version is NOT a contrast
 * problem. `warning.main` has luminance 0.422, so MUI pairs it with black at 9.44:1. This
 * change is conformance, not accessibility. The contrast argument belongs to the ink roles
 * themselves — see DEFAULT_SUCCESS_INK in colorUtils.ts — because the naive way to follow
 * the design (light fill + the `-dark` token, which is what the design system's own
 * `.c-pill--success` does) is what lands at 3.04:1 and 2.60:1.
 */
export const warningBadgeSx = (theme: Theme) => ({
	bgcolor: theme.palette.warning.light,
	color: theme.palette.warningInk.main,
	fontSize: "0.6875rem",
	fontWeight: 600,
	height: "1.25rem",
	borderRadius: "999px",
	"& .MuiChip-label": { px: 1 },
});

/**
 * The Ungrouped section in the roles preview — container and header band.
 *
 * The design gives this section a treatment of its own (`Actions Pattern v2.dc.html:513`):
 * a DASHED `--warning-dark` border around a `--warning-light` header band, with every word
 * in the band set in `--warning-ink` and a `help` icon leading it. Every other section is a
 * plain solid-bordered card.
 *
 * That distinction is the point, and the screen rendered Ungrouped identically to a real
 * group. The section is not an error — an action without a group is a permanent, legitimate
 * state (FR-12b) — but it is also not a group, and a role administrator reading the preview
 * needs to see which sections are configured and which one is the residue. Amber and a
 * dashed edge say "attention, not fault"; `--error-*` would have said fault.
 *
 * The explanatory sentence stays the wording SIMS-8702 AC2 fixed ("These actions stay here
 * until they are assigned to a group, and this section always sorts last."), not the
 * design's shorter "Always sorts last until assigned to a group." — the AC's phrasing says
 * why the section exists as well as where it sorts, and the AC is the authority on copy.
 */
export const ungroupedSectionSx = (theme: Theme) => ({
	p: 0,
	overflow: "hidden",
	borderStyle: "dashed",
	borderColor: theme.palette.warning.dark,
});

export const ungroupedHeaderSx = (theme: Theme) => ({
	bgcolor: theme.palette.warning.light,
	color: theme.palette.warningInk.main,
	borderBottom: `1px solid ${theme.palette.divider}`,
	px: 2,
	py: 1.5,
});
